import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useDemoMode } from "../../../context/DemoModeContext";
import {
    AlertTriangle,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Info,
    Loader2,
    SlidersHorizontal,
    TrendingDown,
    TrendingUp,
    X
} from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { RootURLS } from "constants/in-app-urls";
import { MetricInfoTooltip } from "../MetricInfoTooltip";
import { COPY } from "./copy";
import { SidePanel } from "./SidePanel";
import {
    AVAILABLE_HISTORY_DAYS,
    AttributionDataset,
    CHANNEL_COLORS,
    CHANNEL_LABELS,
    ChannelId,
    PAGE_TYPE_LABELS,
    PageRow,
    TrendMetric,
    TrendSeries
} from "./fixtures/attribution-demo";
import { currencySymbol } from "./setupState";
import type {
    AiSource,
    AttributionPageRow,
    AttributionPages,
    AttributionTrend,
    PageSortKey,
    RevenueSource,
    SortDir,
    TrendGranularity
} from "./attributionReportApi";
import {
    EM_DASH,
    formatApiDate,
    formatCount,
    formatDeltaPct,
    formatMoney,
    formatRate,
    toApiDate
} from "./attributionReportApi";
import { useAttributionReport } from "./useAttributionReport";
import { useAttributionPages } from "./useAttributionPages";
import {
    STALE_AFTER_HOURS,
    SyncStatus,
    formatDate,
    formatDateTime,
    hoursBetween,
    relativeFuture,
    relativeTime
} from "./fixtures/sync-demo";

const PROMPTS_URL = `${RootURLS.HOME_URL}/ai-visibility/prompts`;

const DATE_RANGES = [
    { days: 30, label: "Last 30 days" },
    { days: 60, label: "Last 60 days" },
    { days: 90, label: "Last 90 days" },
    { days: 180, label: "Last 6 months" },
    { days: 365, label: "Last 12 months" }
];

const ALL_CHANNELS: ChannelId[] = ["ai", "direct", "organic"];

/** Weekly buckets per page of the channel trend. */
const TREND_PAGE_SIZE = 6;

// ── Formatting ───────────────────────────────────────────────────────────────

const nf = (n: number) => Math.round(n).toLocaleString("en-US");

function money(n: number, symbol: string, decimals = 0): string {
    return `${symbol}${n.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    })}`;
}

function pct(n: number, decimals = 0): string {
    return `${(n * 100).toFixed(decimals)}%`;
}

/**
 * Axis ticks only. Weekly Organic revenue runs to six figures, and the full
 * "$550,000" string overruns the axis gutter and collides with the plot area.
 */
function compact(n: number, symbol = ""): string {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${symbol}${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    if (abs >= 1_000) return `${symbol}${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
    return `${symbol}${Math.round(n)}`;
}

function signedPct(n: number): string {
    return `${n >= 0 ? "▲" : "▼"} ${Math.abs(n * 100).toFixed(0)}%`;
}

// ── Range slicing ────────────────────────────────────────────────────────────

interface RangeView {
    /** Days actually covered — clamped to the 90 days we hold. */
    days: number;
    /** True when the picker asked for more history than we have (§7 upgrade #1). */
    clamped: boolean;
    current: Record<ChannelId, { sessions: number; conversions: number; revenue: number; valuePerSession: number }>;
    delta: { sessions: number; conversions: number; revenue: number };
    revPerSessionMultiple: number;
    pages: PageRow[];
}

function sliceRange(data: AttributionDataset, requestedDays: number): RangeView {
    const days = Math.min(requestedDays, AVAILABLE_HISTORY_DAYS);
    const clamped = requestedDays > AVAILABLE_HISTORY_DAYS;
    const full = days >= AVAILABLE_HISTORY_DAYS;

    const sumWindow = (points: { ai: number; direct: number; organic: number }[], from: number, to: number) =>
        points
            .slice(from, to)
            .reduce(
                (acc, p) => ({ ai: acc.ai + p.ai, direct: acc.direct + p.direct, organic: acc.organic + p.organic }),
                { ai: 0, direct: 0, organic: 0 }
            );

    const total = data.daily.sessions.length;
    const start = total - days;

    const sessions = sumWindow(data.daily.sessions, start, total);
    const conversions = sumWindow(data.daily.conversions, start, total);
    const revenue = sumWindow(data.daily.revenue, start, total);

    // Previous period: use real data when the window fits twice inside what we hold,
    // otherwise fall back to the stored prior-period totals, scaled to the window.
    const fits = start - days >= 0;
    const scale = days / AVAILABLE_HISTORY_DAYS;
    const prev = fits
        ? {
              sessions: sumWindow(data.daily.sessions, start - days, start),
              conversions: sumWindow(data.daily.conversions, start - days, start),
              revenue: sumWindow(data.daily.revenue, start - days, start)
          }
        : {
              sessions: { ai: data.previous.ai.sessions * scale, direct: 0, organic: 0 },
              conversions: { ai: data.previous.ai.conversions * scale, direct: 0, organic: 0 },
              revenue: { ai: data.previous.ai.revenue * scale, direct: 0, organic: 0 }
          };

    const current = ALL_CHANNELS.reduce((acc, ch) => {
        acc[ch] = {
            sessions: sessions[ch],
            conversions: conversions[ch],
            revenue: revenue[ch],
            valuePerSession: sessions[ch] > 0 ? revenue[ch] / sessions[ch] : 0
        };
        return acc;
    }, {} as RangeView["current"]);

    const change = (now: number, before: number) => (before === 0 ? 0 : (now - before) / before);

    // Page rows scale with the window — the rate stays put, the volume shrinks.
    const sessionShare = data.current.ai.sessions > 0 ? sessions.ai / data.current.ai.sessions : 0;
    const pages: PageRow[] = full
        ? data.pages
        : data.pages.map((p) => ({
              ...p,
              aiSessions: Math.round(p.aiSessions * sessionShare),
              aiConversions: Math.round(p.aiConversions * sessionShare),
              revenue: Math.round(p.revenue * sessionShare)
          }));

    return {
        days,
        clamped,
        current,
        delta: {
            sessions: change(sessions.ai, prev.sessions.ai),
            conversions: change(conversions.ai, prev.conversions.ai),
            revenue: change(revenue.ai, prev.revenue.ai)
        },
        revPerSessionMultiple:
            current.organic.valuePerSession > 0 ? current.ai.valuePerSession / current.organic.valuePerSession : 0,
        pages
    };
}

// ── Small pieces ─────────────────────────────────────────────────────────────

function StatCard({
    label,
    value,
    delta,
    deltaLabel,
    footnote,
    hero,
    muted
}: {
    label: string;
    value: string;
    /** null means there was no prior window — render nothing, never "0%". */
    delta?: number | null;
    deltaLabel?: string | null;
    footnote?: React.ReactNode;
    hero?: boolean;
    /** Thin sample: dim the card, but keep the value — it is still correct. */
    muted?: boolean;
}) {
    const up = (delta ?? 0) >= 0;
    return (
        <div
            className={`rounded-2xl border px-5 py-4 shadow-sm ${
                hero ? "border-geekblue-200 bg-geekblue-100/60 ring-1 ring-geekblue-200" : "border-gray-200/70 bg-white"
            }${muted ? " opacity-60" : ""}`}
        >
            <p className={`text-[11.5px] font-medium ${hero ? "text-geekblue-700" : "text-gray-500"}`}>{label}</p>
            <p
                className={`mt-1 font-semibold tracking-tight tabular-nums ${
                    hero ? "text-[26px] text-geekblue-900" : "text-2xl text-gray-900"
                }`}
            >
                {value}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {delta !== undefined && delta !== null && (
                    <span
                        className={`inline-flex items-center gap-0.5 text-[12px] font-semibold ${
                            up ? "text-emerald-600" : "text-[#A8321F]"
                        }`}
                    >
                        {up ? (
                            <TrendingUp className="h-3 w-3" strokeWidth={2.5} />
                        ) : (
                            <TrendingDown className="h-3 w-3" strokeWidth={2.5} />
                        )}
                        {deltaLabel ?? signedPct(delta)}
                    </span>
                )}
                {footnote && <span className="text-[11.5px] text-gray-500">{footnote}</span>}
            </div>
        </div>
    );
}

function InfoBanner({ onLearnMore }: { onLearnMore: () => void }) {
    return (
        <div className="flex items-start gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-gray-400" strokeWidth={2} />
            <p className="text-[12.5px] leading-relaxed text-gray-600">
                {COPY.honesty.floorBanner}{" "}
                <button
                    type="button"
                    onClick={onLearnMore}
                    className="font-medium text-geekblue-600 underline-offset-2 transition-colors hover:text-geekblue-700 hover:underline"
                >
                    {COPY.honesty.floorBannerLink}
                </button>
            </p>
        </div>
    );
}

function SkippedFixBanner({ onFixNow, onDismiss }: { onFixNow: () => void; onDismiss: () => void }) {
    return (
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-600" strokeWidth={2} />
            <p className="flex-1 text-[12.5px] leading-relaxed text-amber-900">
                {COPY.honesty.skippedFixBanner}{" "}
                <button
                    type="button"
                    onClick={onFixNow}
                    className="font-medium underline underline-offset-2 transition-colors hover:text-amber-950"
                >
                    {COPY.honesty.skippedFixCta}
                </button>
            </p>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                className="-mr-1 rounded p-0.5 text-amber-500 transition-colors hover:bg-amber-100 hover:text-amber-700"
            >
                <X className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
        </div>
    );
}

function ChannelFilter({ selected, onChange }: { selected: ChannelId[]; onChange: (next: ChannelId[]) => void }) {
    const [open, setOpen] = useState(false);
    const allSelected = selected.length === ALL_CHANNELS.length;
    const label = allSelected
        ? "All channels"
        : selected.length === 0
        ? "No channels"
        : selected.map((c) => CHANNEL_LABELS[c]).join(", ");

    // Keep the canonical channel order regardless of the click order.
    const toggle = (ch: ChannelId) =>
        onChange(ALL_CHANNELS.filter((c) => (c === ch ? !selected.includes(ch) : selected.includes(c))));

    return (
        <div className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex h-8 max-w-[240px] items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[12.5px] font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
            >
                <span className="truncate">{label}</span>
                <ChevronDown className="h-3 w-3 flex-none text-gray-400" strokeWidth={2} />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 z-20 mt-1 w-56 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg">
                        <button
                            type="button"
                            onClick={() => onChange(ALL_CHANNELS)}
                            className="flex w-full items-center rounded-md px-2.5 py-1.5 text-left text-[12.5px] text-gray-700 hover:bg-gray-50"
                        >
                            All
                        </button>
                        {ALL_CHANNELS.map((ch) => (
                            <label
                                key={ch}
                                className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[12.5px] text-gray-700 hover:bg-gray-50"
                            >
                                <input
                                    type="checkbox"
                                    className="h-3 w-3 rounded accent-geekblue-600"
                                    checked={selected.includes(ch)}
                                    onChange={() => toggle(ch)}
                                />
                                <span
                                    className="h-2 w-2 flex-none rounded-full"
                                    style={{ backgroundColor: CHANNEL_COLORS[ch] }}
                                />
                                {CHANNEL_LABELS[ch]}
                            </label>
                        ))}
                        <p className="border-t border-gray-100 px-2.5 pb-1 pt-1.5 text-[11px] text-gray-400">
                            Applies to the trend chart.
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}

function SourceMix({ mix }: { mix: AttributionDataset["sourceMix"] }) {
    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-[11.5px] font-medium text-gray-500">{COPY.report.trend.sourceMixLabel}</span>
            <div className="flex h-1.5 min-w-[160px] flex-1 overflow-hidden rounded-full bg-gray-100">
                {mix.map((s) => (
                    <span key={s.name} style={{ width: `${s.share * 100}%`, backgroundColor: s.color }} />
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                {mix.map((s) => (
                    <span key={s.name} className="inline-flex items-center gap-1.5 text-[11.5px] text-gray-600">
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.name}
                        <span className="tabular-nums text-gray-400">{pct(s.share)}</span>
                    </span>
                ))}
            </div>
        </div>
    );
}

/**
 * Live trend -> the bucket shape the chart already draws.
 *
 * Dates stay strings throughout: `points[].date` is a calendar day already
 * resolved in the property's timezone, so putting it through `new Date()` would
 * render a day early for anyone west of UTC.
 */
function liveBuckets(trend: AttributionTrend): TrendSeries[] {
    const byChannel = (c: string) => trend.series.find((s) => s.channel === c)?.points ?? [];
    const ai = byChannel("ai_search");
    const direct = byChannel("direct");
    const organic = byChannel("organic");
    // Series are always present and already zero-filled across every bucket, so
    // the AI series alone defines the axis — no gap-filling or sorting here.
    return ai.map((point, i) => ({
        label: formatApiDate(point.date),
        date: point.date,
        ai: point.value,
        direct: direct[i]?.value ?? 0,
        organic: organic[i]?.value ?? 0
    }));
}

/** Vendors arrive normalised, merged and tail-collapsed; labels are supplied. */
const SOURCE_COLORS = ["#2F54EB", "#597EF7", "#85A5FF", "#ADC6FF", "#D6E4FF"];

function liveSourceMix(sources: AiSource[]): AttributionDataset["sourceMix"] {
    return sources.map((s, i) => ({
        name: s.label,
        share: s.share_pct / 100,
        sessions: s.sessions,
        color: SOURCE_COLORS[i % SOURCE_COLORS.length]
    }));
}

// ── Trend chart ──────────────────────────────────────────────────────────────

function TrendChart({
    data,
    days,
    clamped,
    channels,
    symbol,
    metric,
    onMetricChange,
    granularity,
    onGranularityChange,
    live,
    liveSources,
    allowFixtures
}: {
    data: AttributionDataset;
    days: number;
    clamped: boolean;
    channels: ChannelId[];
    symbol: string;
    metric: TrendMetric;
    onMetricChange: (m: TrendMetric) => void;
    granularity: TrendGranularity;
    onGranularityChange: (g: TrendGranularity) => void;
    /** Non-null in live mode; the chart draws these instead of the fixtures. */
    live: AttributionTrend | null;
    liveSources: AttributionDataset["sourceMix"] | null;
    /** Demo only — live renders an empty chart rather than fixture buckets. */
    allowFixtures: boolean;
}) {
    const [indexed, setIndexed] = useState(false);
    const [hintDismissed, setHintDismissed] = useState(false);
    /** 0 is the most recent page; higher numbers walk backwards in time. */
    const [page, setPage] = useState(0);

    const weeks = Math.max(2, Math.round(days / 7));
    const series = useMemo(
        () => (live ? liveBuckets(live) : allowFixtures ? data.weekly[metric].slice(-weeks) : []),
        [live, data, metric, weeks]
    );

    const pageCount = Math.max(1, Math.ceil(series.length / TREND_PAGE_SIZE));
    // Changing the range changes how many buckets exist; don't strand the viewer
    // on a page that no longer has data.
    const safePage = Math.min(page, pageCount - 1);

    useEffect(() => {
        if (page !== safePage) setPage(safePage);
    }, [page, safePage]);

    const pageSeries = useMemo(() => {
        // Slice from the end so page 0 always holds the most recent buckets.
        const end = series.length - safePage * TREND_PAGE_SIZE;
        return series.slice(Math.max(0, end - TREND_PAGE_SIZE), end);
    }, [series, safePage]);

    const chartData = useMemo(() => {
        if (!indexed) return pageSeries;
        // Index to the first bucket on screen, so the comparison matches what's visible.
        const base = pageSeries[0];
        if (!base) return pageSeries;
        return pageSeries.map((w) => ({
            ...w,
            ai: base.ai ? (w.ai / base.ai) * 100 : 0,
            direct: base.direct ? (w.direct / base.direct) * 100 : 0,
            organic: base.organic ? (w.organic / base.organic) * 100 : 0
        }));
    }, [pageSeries, indexed]);

    const rangeLabel =
        pageSeries.length > 0 ? `${pageSeries[0].label} – ${pageSeries[pageSeries.length - 1].label}` : "";

    const formatValue = (v: number) => {
        if (indexed) return v.toFixed(0);
        if (metric === "revenue") return money(v, symbol);
        return nf(v);
    };

    return (
        <section className="rounded-2xl border border-gray-200/70 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-[15px] font-semibold text-gray-900">{COPY.report.trend.title}</h2>
                    <p className="text-[12.5px] text-gray-500">{COPY.report.trend.subtitle}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                        {(Object.keys(COPY.report.trend.metrics) as TrendMetric[]).map((m) => (
                            <button
                                key={m}
                                type="button"
                                onClick={() => onMetricChange(m)}
                                className={`rounded-md px-3 py-1 text-[12px] font-medium transition-colors ${
                                    metric === m
                                        ? "bg-white text-gray-900 shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                {COPY.report.trend.metrics[m]}
                            </button>
                        ))}
                    </div>
                    <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
                        {(["day", "week"] as TrendGranularity[]).map((g) => (
                            <button
                                key={g}
                                type="button"
                                onClick={() => onGranularityChange(g)}
                                className={`rounded-md px-3 py-1 text-[12px] font-medium capitalize transition-colors ${
                                    granularity === g
                                        ? "bg-white text-gray-900 shadow-sm"
                                        : "text-gray-500 hover:text-gray-700"
                                }`}
                            >
                                {g}
                            </button>
                        ))}
                    </div>
                    <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] font-medium text-gray-600">
                        <input
                            type="checkbox"
                            className="h-3 w-3 rounded accent-geekblue-600"
                            checked={indexed}
                            onChange={(e) => {
                                setIndexed(e.target.checked);
                                setHintDismissed(true);
                            }}
                        />
                        {COPY.report.trend.indexToggle}
                    </label>
                </div>
            </div>

            {/* AI volume is ~1% of Organic, so the raw view flattens it. Nudge once. */}
            {!indexed && !hintDismissed && (
                <p className="mt-2.5 text-[11.5px] italic text-gray-400">{COPY.report.trend.indexHint}</p>
            )}

            <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                {/* Standing legend — the series must be identifiable without hovering. */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                    {ALL_CHANNELS.filter((ch) => channels.includes(ch)).map((ch) => (
                        <span key={ch} className="inline-flex items-center gap-1.5 text-[11.5px] text-gray-600">
                            <span
                                className="h-0.5 w-3.5 rounded-full"
                                style={{ backgroundColor: CHANNEL_COLORS[ch] }}
                            />
                            {CHANNEL_LABELS[ch]}
                        </span>
                    ))}
                </div>

                {pageCount > 1 && (
                    <div className="flex items-center gap-1.5">
                        <span className="text-[11.5px] tabular-nums text-gray-500">{rangeLabel}</span>
                        {/* Older is "previous" here — page 0 is the most recent window. */}
                        <button
                            type="button"
                            onClick={() => setPage((n) => Math.min(n + 1, pageCount - 1))}
                            disabled={safePage >= pageCount - 1}
                            aria-label={COPY.report.trend.olderWeeks}
                            className="rounded-md border border-gray-200 p-1 text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((n) => Math.max(n - 1, 0))}
                            disabled={safePage === 0}
                            aria-label={COPY.report.trend.newerWeeks}
                            className="rounded-md border border-gray-200 p-1 text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                    </div>
                )}
            </div>

            <div className="mt-3 h-[280px] w-full">
                {!live && !allowFixtures ? (
                    <div className="flex h-full flex-col items-center justify-center gap-1 rounded-xl bg-gray-50 text-center">
                        <p className="text-[12.5px] font-medium text-gray-500">{COPY.reportStates.notFetched}</p>
                        <p className="text-[11.5px] text-gray-400">{COPY.reportStates.notFetchedBody}</p>
                    </div>
                ) : channels.length === 0 ? (
                    // Deselecting every channel used to render a silent empty grid.
                    <div className="flex h-full items-center justify-center rounded-xl bg-gray-50 text-[12.5px] text-gray-400">
                        {COPY.report.trend.noChannels}
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                            <XAxis
                                dataKey="label"
                                tick={{ fontSize: 11, fill: "#8C8C8C" }}
                                tickLine={false}
                                axisLine={{ stroke: "#F0F0F0" }}
                            />
                            <YAxis
                                tick={{ fontSize: 11, fill: "#8C8C8C" }}
                                tickLine={false}
                                axisLine={false}
                                width={indexed ? 44 : 56}
                                tickFormatter={(v: number) =>
                                    indexed ? String(v) : compact(v, metric === "revenue" ? symbol : "")
                                }
                            />
                            <Tooltip
                                contentStyle={{
                                    borderRadius: 10,
                                    border: "1px solid #F0F0F0",
                                    fontSize: 12,
                                    boxShadow: "0 4px 16px rgba(0,0,0,0.08)"
                                }}
                                formatter={(value: number, name: string) => [formatValue(value), name]}
                            />
                            {ALL_CHANNELS.filter((ch) => channels.includes(ch)).map((ch) => (
                                <Line
                                    key={ch}
                                    type="monotone"
                                    dataKey={ch}
                                    name={CHANNEL_LABELS[ch]}
                                    stroke={CHANNEL_COLORS[ch]}
                                    strokeWidth={ch === "ai" ? 2.25 : 1.5}
                                    dot={false}
                                    activeDot={{ r: 3.5 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>

            {(() => {
                const mix = liveSources ?? (allowFixtures ? data.sourceMix : []);
                // An empty ai_sources is a normal answer: hide the bar rather than
                // rendering an empty strip or a spinner that never resolves.
                if (mix.length === 0) return null;
                return (
                    <div className="mt-4 border-t border-gray-100 pt-3.5">
                        <SourceMix mix={mix} />
                    </div>
                );
            })()}

            {/* §7 upgrade prompt #1 — only when the picker asked for more than we hold. */}
            {clamped && (
                <p className="mt-3 text-[12px] text-gray-500">
                    <a
                        href="#connect-thrivestack"
                        onClick={(e) => e.preventDefault()}
                        className="text-geekblue-600 underline-offset-2 transition-colors hover:text-geekblue-700 hover:underline"
                    >
                        {COPY.upgrades.history}
                    </a>
                </p>
            )}
        </section>
    );
}

// ── Page table ───────────────────────────────────────────────────────────────

/**
 * One row, whichever source it came from.
 *
 * Demo fixtures and the /pages response are normalised into this shape so the
 * table renders one way. `null` is preserved end to end — it means the value
 * could not be derived, which is not the same as zero.
 */
interface TableRow {
    page: string;
    pageType: string;
    aiSessions: number;
    conversions: number;
    cvr: number | null;
    valuePerSession: number | null;
    revenue: number | null;
    revenueSource: RevenueSource;
    lowSample: boolean;
    /** null = the prompt store was unreachable, distinct from a count of 0. */
    citations: { count: number; prompts: { id: string; text: string }[]; truncated: boolean } | null;
}

function rowsFromFixtures(pages: PageRow[]): TableRow[] {
    return pages
        .filter((p) => p.aiSessions > 0)
        .map((p) => ({
            page: p.path,
            pageType: PAGE_TYPE_LABELS[p.type],
            aiSessions: p.aiSessions,
            conversions: p.aiConversions,
            cvr: p.aiConversions === 0 ? null : p.cvr,
            valuePerSession: p.valuePerSession,
            revenue: p.revenue,
            revenueSource: p.valuePerSession === null ? "modeled" : "modeled",
            lowSample: p.lowConfidence,
            citations: {
                count: p.citedPromptCount,
                prompts: p.prompts.map((text, i) => ({ id: `${p.path}-${i}`, text })),
                truncated: false
            }
        }));
}

function rowsFromApi(rows: AttributionPageRow[], citationsAvailable: boolean): TableRow[] {
    return rows.map((r) => ({
        page: r.page,
        pageType: r.page_type,
        aiSessions: r.ai_sessions,
        conversions: r.conversions,
        cvr: r.cvr,
        valuePerSession: r.value_per_session,
        revenue: r.revenue,
        revenueSource: r.revenue_source,
        lowSample: r.low_sample,
        citations: citationsAvailable && r.citations ? r.citations : null
    }));
}

/** Column -> the sort key the API understands. */
const SORT_KEYS: Record<string, PageSortKey> = {
    aiSessions: "sessions",
    conversions: "conversions",
    cvr: "cvr",
    valuePerSession: "value_per_session",
    revenue: "revenue"
};

/** Money columns can't be ordered when revenue was never configured. */
const MONEY_SORTS: PageSortKey[] = ["revenue", "value_per_session"];

function PageTable({
    pages,
    symbol,
    ga4Revenue,
    live,
    liveLoading,
    allowFixtures,
    sortBy,
    sortDir,
    onSortChange,
    page,
    onPageChange
}: {
    pages: PageRow[];
    symbol: string;
    ga4Revenue: boolean;
    /** Non-null in live mode; fixtures drive the table otherwise. */
    live: AttributionPages | null;
    liveLoading: boolean;
    /** Demo only. Live shows an empty state rather than inventing rows. */
    allowFixtures: boolean;
    sortBy: PageSortKey;
    sortDir: SortDir;
    onSortChange: (key: PageSortKey) => void;
    page: number;
    onPageChange: (page: number) => void;
}) {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState<string | null>(null);

    const meta = live?.meta ?? null;
    const currency = live ? live.currency || meta?.currency || "" : symbol;
    const citationsAvailable = meta ? meta.citations_available : true;
    const revenueConfigured = meta ? meta.revenue_configured : true;

    const rows: TableRow[] = live
        ? rowsFromApi(live.rows, citationsAvailable)
        : allowFixtures
        ? rowsFromFixtures(pages)
        : [];

    const money = (v: number | null, decimals = 0) =>
        live
            ? formatMoney(v, currency, decimals)
            : v === null
            ? EM_DASH
            : `${symbol}${v.toLocaleString("en-US", {
                  minimumFractionDigits: decimals,
                  maximumFractionDigits: decimals
              })}`;

    const th = (col: keyof typeof SORT_KEYS, label: string, extra?: React.ReactNode) => {
        const key = SORT_KEYS[col];
        const active = sortBy === key;
        const disabled = !revenueConfigured && MONEY_SORTS.includes(key);
        return (
            <th
                className="px-4 py-2.5 text-right"
                aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
            >
                <span className="inline-flex items-center gap-1">
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onSortChange(key)}
                        className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-gray-500 transition-colors hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        {label}
                        <span className={`text-[9px] ${active ? "text-geekblue-600" : "text-gray-300"}`}>
                            {active && sortDir === "asc" ? "▲" : "▼"}
                        </span>
                    </button>
                    {extra}
                </span>
            </th>
        );
    };

    const totalRows = live ? live.pagination.total_rows : rows.length;
    const from = live ? (live.pagination.page - 1) * live.pagination.page_size + 1 : 1;
    const to = live ? Math.min(from + rows.length - 1, totalRows) : rows.length;
    const lastPage = live ? Math.max(1, Math.ceil(totalRows / live.pagination.page_size)) : 1;

    return (
        <section className="rounded-2xl border border-gray-200/70 bg-white shadow-sm">
            <header className="px-5 pb-3 pt-5">
                <h2 className="text-[15px] font-semibold text-gray-900">{COPY.report.pageTable.title}</h2>
                <p className="text-[12.5px] text-gray-500">{COPY.report.pageTable.subtitle}</p>
            </header>

            <div className="space-y-2 px-5 pb-3">
                {meta?.no_ai_traffic && (
                    <MetaNotice tone="info">
                        <strong className="font-semibold text-gray-900">{COPY.pageStates.noAiTraffic.title}</strong>{" "}
                        {COPY.pageStates.noAiTraffic.body}
                    </MetaNotice>
                )}
                {meta && !meta.revenue_configured && (
                    <MetaNotice tone="info">{COPY.pageStates.revenueNotConfigured}</MetaNotice>
                )}
                {meta?.other_bucket_present && <MetaNotice tone="warn">{COPY.pageStates.otherBucket}</MetaNotice>}
            </div>

            <div className="overflow-x-auto">
                <table className="w-full min-w-[880px]">
                    <thead className="border-y border-gray-100 bg-gray-50/70">
                        <tr>
                            <th className="px-4 py-2.5 text-left text-[11.5px] font-semibold text-gray-500">
                                {COPY.report.pageTable.columns.page}
                            </th>
                            <th className="px-4 py-2.5 text-left text-[11.5px] font-semibold text-gray-500">
                                {COPY.report.pageTable.columns.type}
                            </th>
                            {th(
                                "aiSessions",
                                COPY.report.pageTable.columns.sessions,
                                <MetricInfoTooltip text={COPY.report.pageTable.sessionsTooltip} />
                            )}
                            {th(
                                "conversions",
                                COPY.report.pageTable.columns.conversions,
                                <MetricInfoTooltip text={COPY.report.pageTable.conversionsTooltip} />
                            )}
                            {th(
                                "cvr",
                                COPY.report.pageTable.columns.cvr,
                                <MetricInfoTooltip text={COPY.report.pageTable.cvrTooltip} />
                            )}
                            {th(
                                "valuePerSession",
                                COPY.report.pageTable.columns.valuePerSession,
                                <MetricInfoTooltip text={COPY.report.pageTable.valuePerSessionTooltip} />
                            )}
                            {th(
                                "revenue",
                                COPY.report.pageTable.columns.revenue,
                                <MetricInfoTooltip text={COPY.report.pageTable.revenueTooltip} />
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {liveLoading && rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-4 py-10 text-center text-[12.5px] text-gray-400">
                                    <Loader2 className="mx-auto h-4 w-4 animate-spin text-gray-300" />
                                </td>
                            </tr>
                        )}
                        {!liveLoading && rows.length === 0 && (
                            <tr>
                                <td colSpan={7} className="px-4 py-10 text-center text-[12.5px] text-gray-400">
                                    {!live && !allowFixtures
                                        ? COPY.reportStates.notFetched
                                        : COPY.report.pageTable.empty}
                                </td>
                            </tr>
                        )}
                        {rows.map((p) => {
                            const isOpen = expanded === p.page;
                            return (
                                <React.Fragment key={p.page}>
                                    <tr className="transition-colors hover:bg-gray-50/60">
                                        <td className="px-4 py-2.5">
                                            <button
                                                type="button"
                                                onClick={() => setExpanded(isOpen ? null : p.page)}
                                                className="flex max-w-[280px] items-center gap-1.5 text-left"
                                            >
                                                <ChevronRight
                                                    className={`h-3.5 w-3.5 flex-none text-gray-300 transition-transform ${
                                                        isOpen ? "rotate-90 text-gray-500" : ""
                                                    }`}
                                                    strokeWidth={2}
                                                />
                                                <span className="truncate font-mono text-[12px] text-gray-900">
                                                    {p.page}
                                                </span>
                                            </button>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] font-medium capitalize text-gray-600">
                                                {p.pageType}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2.5 text-right text-[12.5px] tabular-nums text-gray-900">
                                            {formatCount(p.aiSessions)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right text-[12.5px] tabular-nums text-gray-900">
                                            {formatCount(p.conversions)}
                                        </td>
                                        <td className="px-4 py-2.5 text-right text-[12.5px] tabular-nums text-gray-600">
                                            {p.cvr === null ? (
                                                <span
                                                    tabIndex={0}
                                                    title={COPY.report.pageTable.noObservedConversions}
                                                    className="cursor-help text-gray-400"
                                                >
                                                    {EM_DASH}
                                                </span>
                                            ) : (
                                                formatRate(p.cvr)
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            {p.valuePerSession === null ? (
                                                <span
                                                    tabIndex={0}
                                                    title={COPY.honesty.lowSampleTooltip}
                                                    className="cursor-help text-[12.5px] text-gray-400"
                                                >
                                                    {EM_DASH}
                                                </span>
                                            ) : (
                                                <span
                                                    tabIndex={p.lowSample ? 0 : undefined}
                                                    title={
                                                        p.lowSample
                                                            ? COPY.pageStates.lowSample(meta?.low_sample_threshold ?? 0)
                                                            : undefined
                                                    }
                                                    className={`text-[12.5px] tabular-nums ${
                                                        p.lowSample
                                                            ? "cursor-help text-gray-500 underline decoration-dotted underline-offset-2"
                                                            : "text-gray-900"
                                                    }`}
                                                >
                                                    {money(p.valuePerSession, 2)}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2.5 text-right">
                                            <span
                                                tabIndex={0}
                                                title={COPY.pageStates.revenueSource[p.revenueSource]}
                                                className="cursor-help text-[12.5px] font-medium tabular-nums text-gray-900"
                                            >
                                                {money(p.revenue)}
                                            </span>
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr className="bg-gray-50/70">
                                            <td colSpan={7} className="px-4 py-3.5 pl-11">
                                                <p className="text-[11.5px] font-semibold text-gray-500">
                                                    {COPY.report.pageTable.expandPrompts}
                                                </p>
                                                {!p.citations || p.citations.prompts.length === 0 ? (
                                                    <p className="mt-1.5 text-[12px] text-gray-400">
                                                        {COPY.report.pageTable.notCited}
                                                    </p>
                                                ) : (
                                                    <>
                                                        <ul className="mt-2 flex flex-wrap gap-1.5">
                                                            {p.citations.prompts.map((prompt) => (
                                                                <li
                                                                    key={prompt.id}
                                                                    className="rounded-md border border-gray-200 bg-white px-2 py-1 text-[11.5px] text-gray-700"
                                                                >
                                                                    {prompt.text}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        {p.citations.truncated && (
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    navigate(
                                                                        `${PROMPTS_URL}?page=${encodeURIComponent(
                                                                            p.page
                                                                        )}`
                                                                    )
                                                                }
                                                                className="mt-2 text-[11.5px] font-medium text-geekblue-600 hover:underline"
                                                            >
                                                                {COPY.pageStates.viewAllPrompts(p.citations.count)}
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>

                    {/* Totals come from the API — summing `rows` would only total one page. */}
                    {live && (
                        <tfoot className="border-t border-gray-200 bg-gray-50/70">
                            <tr>
                                <td colSpan={2} className="px-4 py-2.5 text-[11.5px] font-semibold text-gray-500">
                                    {COPY.pageStates.totalsLabel}
                                </td>
                                <td className="px-4 py-2.5 text-right text-[12.5px] font-semibold tabular-nums text-gray-900">
                                    {formatCount(live.totals.ai_sessions)}
                                </td>
                                <td className="px-4 py-2.5 text-right text-[12.5px] font-semibold tabular-nums text-gray-900">
                                    {formatCount(live.totals.conversions)}
                                </td>
                                <td />
                                <td />
                                <td className="px-4 py-2.5 text-right text-[12.5px] font-semibold tabular-nums text-gray-900">
                                    {formatMoney(live.totals.revenue, currency)}
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {live && totalRows > live.pagination.page_size && (
                <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-5 py-2.5">
                    <span className="text-[11.5px] tabular-nums text-gray-500">
                        {COPY.pageStates.rowsRange(from, to, totalRows)}
                    </span>
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            aria-label={COPY.pageStates.prev}
                            disabled={page <= 1 || liveLoading}
                            onClick={() => onPageChange(page - 1)}
                            className="rounded-md border border-gray-200 p-1 text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                        <button
                            type="button"
                            aria-label={COPY.pageStates.next}
                            disabled={page >= lastPage || liveLoading}
                            onClick={() => onPageChange(page + 1)}
                            className="rounded-md border border-gray-200 p-1 text-gray-500 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-gray-100 px-5 py-3">
                <p className="text-[11.5px] text-gray-400">{COPY.honesty.attributionModelLabel}</p>
                {!ga4Revenue && rows.some((p) => p.valuePerSession === null) && (
                    <a
                        href="#connect-stripe"
                        onClick={(e) => e.preventDefault()}
                        className="text-[11.5px] text-geekblue-600 underline-offset-2 transition-colors hover:text-geekblue-700 hover:underline"
                    >
                        {COPY.upgrades.stripe}
                    </a>
                )}
            </div>
        </section>
    );
}

// ── Sync status (§4, §5) ─────────────────────────────────────────────────────

function SyncPopoverRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-4">
            <span className="text-[11.5px] text-gray-500">{label}</span>
            <span className="text-right text-[11.5px] tabular-nums text-gray-900">{value}</span>
        </div>
    );
}

function SyncFailurePill({ status, onOpen }: { status: SyncStatus; onOpen: () => void }) {
    return (
        <button
            type="button"
            onClick={onOpen}
            className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-[11.5px] font-medium text-amber-800 transition-colors hover:bg-amber-100"
        >
            <AlertTriangle className="h-3 w-3" strokeWidth={2} />
            {COPY.sync.failedPill(relativeTime(status.lastSyncAt))}
        </button>
    );
}

function SyncFailurePanel({
    open,
    onClose,
    status,
    propertyIds,
    onReconnect
}: {
    open: boolean;
    onClose: () => void;
    status: SyncStatus;
    propertyIds: string[];
    onReconnect: () => void;
}) {
    const cause = status.failureCause;
    if (!cause) return null;
    const failure = COPY.sync.failures[cause];
    // Each cause gets its own message and fix path — a generic "something went
    // wrong" tells the customer nothing they can act on.
    const message = typeof failure.message === "function" ? failure.message(propertyIds[0] ?? "—") : failure.message;

    return (
        <SidePanel open={open} title={COPY.sync.failurePanelTitle} onClose={onClose}>
            <p className="text-[12.5px] leading-relaxed text-gray-700">{message}</p>
            <div className="mt-4 space-y-1.5 rounded-lg bg-gray-50 p-3.5">
                <SyncPopoverRow label={COPY.sync.popover.lastSync} value={formatDateTime(status.lastSyncAt)} />
                <SyncPopoverRow label={COPY.sync.popover.dataThrough} value={formatDate(status.dataThrough)} />
                <SyncPopoverRow
                    label={propertyIds.length === 1 ? COPY.sync.popover.property : COPY.sync.popover.properties}
                    value={<span className="font-mono">{propertyIds.join(", ") || "—"}</span>}
                />
            </div>
            {failure.action && (
                <button
                    type="button"
                    onClick={() => {
                        onClose();
                        onReconnect();
                    }}
                    className="mt-4 rounded-lg bg-geekblue-600 px-4 py-2 text-[12.5px] font-medium text-white transition-colors hover:bg-geekblue-700"
                >
                    {failure.action}
                </button>
            )}
        </SidePanel>
    );
}

function StaleBanner({ status, onDismiss }: { status: SyncStatus; onDismiss: () => void }) {
    return (
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-600" strokeWidth={2} />
            <p className="flex-1 text-[12.5px] leading-relaxed text-amber-900">
                {COPY.sync.staleBanner(formatDate(status.dataThrough))}
            </p>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss"
                className="-mr-1 rounded p-0.5 text-amber-500 transition-colors hover:bg-amber-100 hover:text-amber-700"
            >
                <X className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
        </div>
    );
}

// ── Learn-more panel ─────────────────────────────────────────────────────────

function FloorPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
    const p = COPY.honesty.floorPanel;
    return (
        <SidePanel open={open} title={p.title} onClose={onClose}>
            <div className="space-y-5">
                {p.sections.map((s) => (
                    <div key={s.title}>
                        <h3 className="text-[13px] font-semibold text-gray-900">{s.title}</h3>
                        <p className="mt-1 text-[12.5px] leading-relaxed text-gray-600">{s.body}</p>
                    </div>
                ))}
                <p className="rounded-lg border-l-2 border-geekblue-400 bg-geekblue-100/50 px-3.5 py-2.5 text-[12.5px] leading-relaxed text-gray-700">
                    {p.closing}
                </p>
            </div>
        </SidePanel>
    );
}

/** Neutral strip for the 200-with-a-body states. Only a 400 is an error. */
function MetaNotice({ tone, children }: { tone: "info" | "warn"; children: React.ReactNode }) {
    const warn = tone === "warn";
    return (
        <div
            className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 ${
                warn ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-gray-50"
            }`}
        >
            {warn ? (
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none text-amber-600" strokeWidth={2} />
            ) : (
                <Info className="mt-0.5 h-3.5 w-3.5 flex-none text-gray-400" strokeWidth={2} />
            )}
            <p className={`text-[12.5px] leading-relaxed ${warn ? "text-amber-900" : "text-gray-600"}`}>{children}</p>
        </div>
    );
}

// ── Report ───────────────────────────────────────────────────────────────────

export function RevenueAttributionReport({
    data,
    currency,
    ga4Revenue,
    channelSkipped,
    onFixChannel,
    syncStatus,
    propertyIds,
    onReconnect,
    onOpenConnection,
    onEditSetup,
    onConfiguredChange
}: {
    data: AttributionDataset;
    currency: string;
    ga4Revenue: boolean;
    channelSkipped: boolean;
    onFixChannel: () => void;
    syncStatus: SyncStatus;
    propertyIds: string[];
    onReconnect: () => void;
    onOpenConnection: () => void;
    onEditSetup: () => void;
    /** Fires when /summary reports the tenant isn't configured after all. */
    onConfiguredChange?: (configured: boolean) => void;
}) {
    const { isDemoMode } = useDemoMode();
    const [rangeDays, setRangeDays] = useState(90);
    const [metric, setMetric] = useState<TrendMetric>("sessions");
    const [granularity, setGranularity] = useState<TrendGranularity>("week");
    // Page table sort/paging live here because the API owns both — every change
    // is a refetch, never a client-side reorder or slice.
    const [pageSortBy, setPageSortBy] = useState<PageSortKey>("revenue");
    const [pageSortDir, setPageSortDir] = useState<SortDir>("desc");
    const [pageNumber, setPageNumber] = useState(1);
    const PAGE_SIZE = 25;
    const [channels, setChannels] = useState<ChannelId[]>(ALL_CHANNELS);
    const [floorOpen, setFloorOpen] = useState(false);
    const [warningDismissed, setWarningDismissed] = useState(false);
    const [failurePanelOpen, setFailurePanelOpen] = useState(false);
    const [staleDismissed, setStaleDismissed] = useState(false);

    // Anything older than the stale window gets said out loud, not served quietly.
    const isStale =
        syncStatus.health !== "HEALTHY" && hoursBetween(new Date(), syncStatus.lastSyncAt) > STALE_AFTER_HOURS;

    const symbol = currencySymbol(currency);
    const view = useMemo(() => sliceRange(data, rangeDays), [data, rangeDays]);

    // Demo runs entirely on fixtures and must never touch the network.
    const { startDate, endDate } = useMemo(() => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - (rangeDays - 1));
        return { startDate: toApiDate(start), endDate: toApiDate(end) };
    }, [rangeDays]);

    const report = useAttributionReport(
        { startDate, endDate, attributionScope: "session", metric, granularity },
        !isDemoMode
    );

    const summary = report.summary;
    const trend = report.trend;
    const summaryMeta = summary?.meta ?? null;
    const meta = summaryMeta ?? trend?.meta ?? null;
    const lowSample = Boolean(summaryMeta?.low_sample);
    const liveCurrency = summary?.ai_revenue.currency ?? meta?.currency ?? "";
    const liveSources = trend ? liveSourceMix(trend.ai_sources) : null;

    const pagesQuery = useAttributionPages(
        {
            startDate,
            endDate,
            attributionScope: "session",
            sortBy: pageSortBy,
            sortDir: pageSortDir,
            page: pageNumber,
            pageSize: PAGE_SIZE
        },
        !isDemoMode
    );

    // A new sort or window describes a different list; page 1 is the only
    // position that still means anything.
    const changePageSort = (key: PageSortKey) => {
        setPageSortDir((d) => (pageSortBy === key ? (d === "desc" ? "asc" : "desc") : "desc"));
        setPageSortBy(key);
        setPageNumber(1);
    };

    useEffect(() => {
        setPageNumber(1);
    }, [startDate, endDate]);

    useEffect(() => {
        if (summaryMeta) onConfiguredChange?.(summaryMeta.configured);
    }, [summaryMeta, onConfiguredChange]);

    return (
        <div className="space-y-5">
            {/*
             * §5 — sticky global controls.
             * Solid background on purpose: `backdrop-blur` makes this element a
             * containing block for `position: fixed`, which trapped the channel
             * dropdown's full-screen click-catcher inside this bar and stopped
             * click-outside from ever closing it.
             */}
            {/*
             * No negative margins: `-mx-6 px-6` bled 24px past the content column on
             * each side, so this bar sat visibly wider than every card below it.
             * The page container already supplies the gutter.
             */}
            <div className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white py-3">
                <div className="flex flex-wrap items-center gap-2">
                    <select
                        aria-label={COPY.report.dateRangeLabel}
                        value={rangeDays}
                        onChange={(e) => setRangeDays(Number(e.target.value))}
                        className="h-8 rounded-lg border border-gray-200 bg-white px-2.5 text-[12.5px] font-medium text-gray-700 transition-colors hover:border-gray-300 focus:outline-none"
                    >
                        {DATE_RANGES.map((r) => (
                            <option key={r.days} value={r.days}>
                                {r.label}
                            </option>
                        ))}
                    </select>
                    <ChannelFilter selected={channels} onChange={setChannels} />
                </div>
                {/* A failed sync still needs saying; the routine "updated N hours
                    ago" line did not, so only the failure pill survives here. */}
                <div className="flex min-w-0 items-center gap-3">
                    {syncStatus.health === "FAILED" && (
                        <SyncFailurePill status={syncStatus} onOpen={() => setFailurePanelOpen(true)} />
                    )}
                    {/* Connection settings used to live behind the sync popover — kept
                        reachable now that it's gone. */}
                    <button
                        type="button"
                        onClick={onOpenConnection}
                        className="inline-flex h-8 flex-none items-center gap-1.5 rounded-lg px-2 text-[12.5px] font-medium text-gray-500 transition-colors hover:text-gray-700"
                    >
                        {COPY.connection.openLink}
                    </button>
                    {/* Values like deal size get revised — the wizard stays reachable. */}
                    <button
                        type="button"
                        onClick={onEditSetup}
                        className="inline-flex h-8 flex-none items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-[12.5px] font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50"
                    >
                        <SlidersHorizontal className="h-3.5 w-3.5 text-gray-400" strokeWidth={2} />
                        {COPY.report.editSetup}
                    </button>
                </div>
            </div>

            {isStale && !staleDismissed && (
                <StaleBanner status={syncStatus} onDismiss={() => setStaleDismissed(true)} />
            )}

            {channelSkipped && !warningDismissed && (
                <SkippedFixBanner onFixNow={onFixChannel} onDismiss={() => setWarningDismissed(true)} />
            )}

            {/* Every state below arrives as a 200 body, not an error. */}
            {(report.error || pagesQuery.error) && <MetaNotice tone="warn">{COPY.reportStates.badRequest}</MetaNotice>}

            {summaryMeta?.no_data && (
                <MetaNotice tone="info">
                    <strong className="font-semibold text-gray-900">{COPY.reportStates.noData.title}</strong>{" "}
                    {COPY.reportStates.noData.body}
                </MetaNotice>
            )}

            {lowSample && (
                <MetaNotice tone="info">
                    {COPY.reportStates.lowSample(summaryMeta?.low_sample_threshold ?? 0)}
                </MetaNotice>
            )}

            {trend?.meta.excluded_sessions ? (
                <MetaNotice tone="info">{COPY.reportStates.excludedSessions(trend.meta.excluded_sessions)}</MetaNotice>
            ) : null}

            <InfoBanner onLearnMore={() => setFloorOpen(true)} />

            {/* §5.1 */}
            {report.summaryLoading && !summary ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="h-[92px] animate-pulse rounded-2xl bg-gray-100" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard
                        label={COPY.report.stats.sessions}
                        value={
                            summary
                                ? formatCount(summary.ai_sessions.value)
                                : isDemoMode
                                ? nf(view.current.ai.sessions)
                                : EM_DASH
                        }
                        deltaLabel={summary ? formatDeltaPct(summary.ai_sessions.delta_pct) : undefined}
                        delta={summary ? summary.ai_sessions.delta_pct : isDemoMode ? view.delta.sessions : null}
                        footnote={COPY.report.stats.vsPrev}
                    />
                    <StatCard
                        label={COPY.report.stats.conversions}
                        value={
                            summary
                                ? formatCount(summary.ai_conversions.value)
                                : isDemoMode
                                ? nf(view.current.ai.conversions)
                                : EM_DASH
                        }
                        deltaLabel={summary ? formatDeltaPct(summary.ai_conversions.delta_pct) : undefined}
                        delta={summary ? summary.ai_conversions.delta_pct : isDemoMode ? view.delta.conversions : null}
                    />
                    <StatCard
                        label={COPY.report.stats.revenue}
                        value={
                            summary
                                ? formatMoney(summary.ai_revenue.value, liveCurrency)
                                : isDemoMode
                                ? money(view.current.ai.revenue, symbol)
                                : EM_DASH
                        }
                        deltaLabel={summary ? formatDeltaPct(summary.ai_revenue.delta_pct) : undefined}
                        delta={summary ? summary.ai_revenue.delta_pct : isDemoMode ? view.delta.revenue : null}
                        muted={lowSample}
                    />
                    <StatCard
                        hero
                        muted={lowSample}
                        label={COPY.report.stats.revPerSession}
                        value={
                            summary
                                ? formatMoney(summary.revenue_per_session.ai, liveCurrency, 2)
                                : isDemoMode
                                ? money(view.current.ai.valuePerSession, symbol, 2)
                                : EM_DASH
                        }
                        footnote={
                            <span className="inline-flex items-center gap-1.5">
                                {COPY.report.stats.vsOrganic(
                                    summary
                                        ? formatMoney(summary.revenue_per_session.organic, liveCurrency, 2)
                                        : isDemoMode
                                        ? money(view.current.organic.valuePerSession, symbol, 2)
                                        : EM_DASH
                                )}
                                {summary?.meta.organic_includes_branded_search && (
                                    <MetricInfoTooltip text={COPY.reportStates.brandedSearch} />
                                )}
                                {/*
                                 * No trend arrow here: the other cards use ▲ for change over
                                 * time, and reusing it for a ratio against Organic made this
                                 * read as "up 3x since last period". Rendered as a chip instead.
                                 */}
                                <span className="rounded-full bg-geekblue-600 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                                    {summary
                                        ? summary.revenue_per_session.multiple === null
                                            ? EM_DASH
                                            : `${summary.revenue_per_session.multiple.toFixed(1)}×`
                                        : isDemoMode
                                        ? `${view.revPerSessionMultiple.toFixed(1)}×`
                                        : EM_DASH}
                                </span>
                            </span>
                        }
                    />
                </div>
            )}

            {/* §5.2 */}
            <TrendChart
                data={data}
                days={view.days}
                clamped={!isDemoMode ? false : view.clamped}
                channels={channels}
                symbol={summary ? "" : symbol}
                metric={metric}
                onMetricChange={setMetric}
                granularity={granularity}
                onGranularityChange={setGranularity}
                live={trend}
                liveSources={liveSources}
                allowFixtures={isDemoMode}
            />

            {/* §5.3 */}
            <PageTable
                pages={view.pages}
                symbol={symbol}
                ga4Revenue={ga4Revenue}
                live={pagesQuery.data}
                liveLoading={pagesQuery.loading}
                allowFixtures={isDemoMode}
                sortBy={pageSortBy}
                sortDir={pageSortDir}
                onSortChange={changePageSort}
                page={pageNumber}
                onPageChange={setPageNumber}
            />

            <FloorPanel open={floorOpen} onClose={() => setFloorOpen(false)} />
            <SyncFailurePanel
                open={failurePanelOpen}
                onClose={() => setFailurePanelOpen(false)}
                status={syncStatus}
                propertyIds={propertyIds}
                onReconnect={onReconnect}
            />
        </div>
    );
}

export default RevenueAttributionReport;
